import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { Masked } from "work/_shared/masked-fields";
import WorkAgreement from "work/work-agreements/work-agreement";

const ListContainer = styled(Grid)(({ theme }) => ({
  flexWrap: "nowrap",
  height: "100%",
  marginLeft: theme.spacing(3),
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type ConflictsTabProps = {
  workAgreement: WorkAgreement;
};

export default function ConflictsTab(props: Readonly<ConflictsTabProps>) {
  const { workAgreement } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsDocument = await documentApiService.downloadDocument(documentId);
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download document. Please try again later.", { variant: "error" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (!workAgreement) return null;

  if (Masked.isMasked(workAgreement.conflictsCheckWaived) || Masked.isMasked(workAgreement.conflicts)) {
    return "You do not have permission to view contracts.";
  }

  return (
    <ListContainer container direction="row">
      <Grid>
        {workAgreement?.conflictsCheckWaived.value ? (
          <HelperText>Conflicts check waived</HelperText>
        ) : (
          <>
            {workAgreement.conflicts.length === 0 && (
              <HelperText>No conflicts documents</HelperText>
            )}
            {workAgreement.conflicts.length > 0 && (
              <DocumentList
                documents={workAgreement?.conflicts ?? []}
                documentType={WorkDocumentType.Conflicts}
                downloadingFileId={isDownloadingFile}
                hideCommentsButton={true}
                onDownload={handleDownloadDocumentById}
              />
            )}
          </>
        )}
      </Grid>
    </ListContainer>
  );
}
